import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { YoutubePreview } from '@/components/content/youtube-preview';
import { ChurchLogoUploader } from '@/components/shared/church-logo-uploader';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import {
  EntityCombobox,
  type EntityOption,
} from '@/components/shared/entity-combobox';
import { FileDropzone } from '@/components/shared/file-dropzone';
import { PermissionGate } from '@/components/shared/permission-gate';
import { PERMISSIONS } from '@/lib/permissions';
import {
  ScriptureRefPicker,
  scriptureRefPickerLabels,
} from '@/components/shared/scripture-ref-picker';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { SwitchField } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { uploadBlob } from '@/lib/upload';
import { ContentPageHeader } from '@/pages/content/content-page-header';
import { useContentPeriodStore } from '@/pages/content/content-period-store';
import { formatDate, formatDateTime } from '@/utils/format';

type SermonStatus = 'draft' | 'scheduled' | 'published';
type PublishTiming = 'draft' | 'schedule';

interface Sermon {
  id: string;
  title: string;
  preacher: string | null;
  preachedOn: string | null;
  occurrenceId: string | null;
  programId: string | null;
  programName: string | null;
  occurrenceDate: string | null;
  description: string | null;
  scriptureRefs: string[];
  audioKey: string | null;
  audioUrl: string | null;
  videoUrl: string | null;
  artworkKey: string | null;
  artworkUrl: string | null;
  isDownloadable: boolean;
  status: SermonStatus;
  publishAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  canDelete?: boolean;
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface ServiceProgram {
  id: string;
  name: string;
}

interface OccurrenceOption {
  id: string;
  programId: string;
  programName: string | null;
  occurrenceDate: string;
}

const guestSpeakerId = (name: string) => `guest:${name}`;

const emptyForm = () => ({
  title: '',
  preacher: '',
  preachedOn: null as string | null,
  programId: null as string | null,
  occurrenceId: null as string | null,
  description: '',
  verse1: '',
  verse2: '',
  verse3: '',
  videoUrl: '',
  artworkKey: null as string | null,
  artworkPreviewUrl: null as string | null,
  audioKey: null as string | null,
  audioName: null as string | null,
  audioDurationSeconds: null as number | null,
  isDownloadable: true,
  publishTiming: 'draft' as PublishTiming,
  publishAt: null as string | null,
});

const audioDurationSeconds = (file: File): Promise<number | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      const seconds = Number.isFinite(audio.duration) ? Math.round(audio.duration) : null;
      URL.revokeObjectURL(url);
      resolve(seconds);
    });
    audio.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      resolve(null);
    });
  });

const serviceLabel = (
  programName: string | null | undefined,
  occurrenceDate: string | null | undefined,
): string => {
  if (!programName && !occurrenceDate) return '—';
  if (programName && occurrenceDate) {
    return `${programName} · ${formatDate(occurrenceDate)}`;
  }
  return programName ?? (occurrenceDate ? formatDate(occurrenceDate) : '—');
};

const publishDisplay = (row: Sermon): string | null => row.publishAt ?? row.publishedAt;

export const SermonsPage = () => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Sermon | null>(null);
  const [detail, setDetail] = useState<Sermon | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [speakerId, setSpeakerId] = useState<string | null>(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState<EntityOption | null>(null);
  const [speakerQuery, setSpeakerQuery] = useState('');
  const [audioUploading, setAudioUploading] = useState(false);
  const [publishTarget, setPublishTarget] = useState<Sermon | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Sermon | null>(null);

  const dateRange = useContentPeriodStore((state) => state.dateRange);

  const list = useQuery({
    queryKey: ['content', 'sermons', dateRange.from, dateRange.to],
    queryFn: () =>
      api.get<{ data: Sermon[] }>('/content/sermons', {
        query: {
          dateFrom: dateRange.from ?? undefined,
          dateTo: dateRange.to ?? undefined,
        },
      }),
  });

  const speakers = useQuery({
    queryKey: ['people', 'members', 'picker', 'sermon-speaker', speakerQuery],
    queryFn: () =>
      api.get<{ data: MemberOption[] }>(
        `/people/members?pageSize=20&status=active${
          speakerQuery ? `&q=${encodeURIComponent(speakerQuery)}` : ''
        }`,
      ),
    enabled: formOpen,
    placeholderData: keepPreviousData,
  });

  const servicePrograms = useQuery({
    queryKey: ['programs', 'service', 'sermon-picker'],
    queryFn: () =>
      api.get<{ data: ServiceProgram[] }>('/programs?kind=service&pageSize=100'),
    enabled: formOpen,
  });

  const serviceDates = useQuery({
    queryKey: ['occurrences', 'service', form.programId, 'sermon-picker'],
    queryFn: () =>
      api.get<{ data: OccurrenceOption[] }>(
        `/occurrences?kind=service&programId=${form.programId}&pageSize=100`,
      ),
    enabled: formOpen && Boolean(form.programId),
  });

  const speakerOptions = useMemo(
    () =>
      (speakers.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [speakers.data?.data],
  );

  const programOptions = useMemo(
    () =>
      (servicePrograms.data?.data ?? []).map((program) => ({
        value: program.id,
        label: program.name,
      })),
    [servicePrograms.data?.data],
  );

  const dateOptions = useMemo(
    () =>
      (serviceDates.data?.data ?? []).map((row) => ({
        value: row.id,
        label: formatDate(row.occurrenceDate),
      })),
    [serviceDates.data?.data],
  );

  const scheduleInPast =
    form.publishTiming === 'schedule' &&
    form.publishAt !== null &&
    new Date(form.publishAt).getTime() <= Date.now();

  const resetForm = () => {
    setForm(emptyForm());
    setEditing(null);
    setSpeakerId(null);
    setSelectedSpeaker(null);
    setSpeakerQuery('');
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (sermon: Sermon) => {
    const verses = sermon.scriptureRefs ?? [];
    const preacher = sermon.preacher?.trim() ?? '';
    setEditing(sermon);
    setForm({
      title: sermon.title,
      preacher,
      preachedOn: sermon.preachedOn,
      programId: sermon.programId,
      occurrenceId: sermon.occurrenceId,
      description: sermon.description ?? '',
      verse1: verses[0] ?? '',
      verse2: verses[1] ?? '',
      verse3: verses[2] ?? '',
      videoUrl: sermon.videoUrl ?? '',
      artworkKey: sermon.artworkKey,
      artworkPreviewUrl: sermon.artworkUrl,
      audioKey: sermon.audioKey,
      audioName: sermon.audioKey ? t('sermons.audioAttached') : null,
      audioDurationSeconds: null,
      isDownloadable: sermon.isDownloadable,
      publishTiming: sermon.status === 'scheduled' ? 'schedule' : 'draft',
      publishAt: sermon.publishAt,
    });
    if (preacher) {
      const option = { id: guestSpeakerId(preacher), label: preacher };
      setSpeakerId(option.id);
      setSelectedSpeaker(option);
    } else {
      setSpeakerId(null);
      setSelectedSpeaker(null);
    }
    setDetail(null);
    setFormOpen(true);
  };

  const buildPayload = () => {
    const scriptureRefs = [form.verse1, form.verse2, form.verse3]
      .map((verse) => verse.trim())
      .filter(Boolean);
    return {
      title: form.title.trim(),
      preacher: form.preacher.trim() || null,
      preachedOn: form.preachedOn,
      occurrenceId: form.occurrenceId,
      description: form.description.trim() || null,
      scriptureRefs,
      artworkKey: form.artworkKey,
      audioKey: form.audioKey,
      audioDurationSeconds: form.audioDurationSeconds,
      videoUrl: form.videoUrl.trim() || null,
      isDownloadable: form.isDownloadable,
      ...(editing?.status === 'published'
        ? {}
        : { publishAt: form.publishTiming === 'schedule' ? form.publishAt : null }),
    };
  };

  const save = useMutation({
    mutationFn: () => {
      const body = buildPayload();
      if (editing) return api.patch<Sermon>(`/content/sermons/${editing.id}`, body);
      return api.post<Sermon>('/content/sermons', body);
    },
    onSuccess: (sermon) => {
      toast.success(
        sermon.status === 'scheduled' ? t('sermons.scheduled') : t('sermons.saved'),
      );
      setFormOpen(false);
      resetForm();
      setDetail(sermon);
      void queryClient.invalidateQueries({ queryKey: ['content', 'sermons'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post<Sermon>(`/content/sermons/${id}/publish`),
    onSuccess: (sermon) => {
      toast.success(t('sermons.published'));
      setPublishTarget(null);
      setDetail(sermon);
      void queryClient.invalidateQueries({ queryKey: ['content', 'sermons'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.post<Sermon>(`/content/sermons/${id}/unpublish`),
    onSuccess: (sermon) => {
      toast.success(t('sermons.unpublished'));
      setDetail(sermon);
      void queryClient.invalidateQueries({ queryKey: ['content', 'sermons'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/content/sermons/${id}`),
    onSuccess: () => {
      toast.success(t('sermons.deleted'));
      setDeleteTarget(null);
      setDetail(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'sermons'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Sermon>[] = [
    {
      id: 'createdAt',
      header: t('sermons.columns.createdAt'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'publishAt',
      header: t('sermons.columns.publishAt'),
      cell: (row) => {
        const value = publishDisplay(row);
        return value ? formatDateTime(value) : '—';
      },
    },
    {
      id: 'service',
      header: t('sermons.columns.service'),
      cell: (row) => serviceLabel(row.programName, row.occurrenceDate),
      hideBelowMd: true,
    },
    {
      id: 'title',
      header: t('sermons.columns.title'),
      cell: (row) => row.title,
    },
    {
      id: 'preacher',
      header: t('sermons.columns.preacher'),
      cell: (row) => row.preacher ?? '—',
    },
    {
      id: 'status',
      header: t('sermons.columns.status'),
      cell: (row) => (
        <Badge
          variant={
            row.status === 'published'
              ? 'success'
              : row.status === 'scheduled'
                ? 'info'
                : 'neutral'
          }
        >
          {t(`status.${row.status}`)}
        </Badge>
      ),
    },
  ];

  const rows = list.data?.data ?? [];
  const editingPublished = editing?.status === 'published';
  const canSave =
    form.title.trim().length > 0 &&
    (editingPublished ||
      (!scheduleInPast && (form.publishTiming === 'draft' || form.publishAt !== null))) &&
    !save.isPending &&
    !audioUploading;

  const detailShowPublish = detail?.status === 'draft' || detail?.status === 'scheduled';

  return (
    <div>
      <ContentPageHeader
        actions={
          <PermissionGate permission={PERMISSIONS.CONTENT_CREATE}>
            <Button onClick={openCreate}>{t('sermons.create')}</Button>
          </PermissionGate>
        }
      />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('sermons.empty')}</p>}
        onRowClick={(row) => setDetail(row)}
        rowActions={(row) =>
          row.canDelete === true ? (
            <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDeleteTarget(row)}
              >
                {tCommon('action.delete')}
              </Button>
            </PermissionGate>
          ) : null
        }
        actionsHeader={tCommon('table.actions')}
      />

      <Dialog
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{detail?.title ?? t('sermons.title')}</DialogTitle>
          </DialogHeader>
          {detail ? (
            <>
              <DialogBody contentClassName="flex flex-col gap-6">
                {detail.videoUrl ? (
                  <YoutubePreview
                    url={detail.videoUrl}
                    title={t('sermons.previewTitle', { title: detail.title })}
                    unavailableLabel={t('sermons.previewUnavailable')}
                    watchLabel={t('sermons.watchOnYoutube')}
                  />
                ) : null}
                {detail.audioUrl ? (
                  <div>
                    <p className="text-caption text-fg-secondary">{t('sermons.audioLabel')}</p>
                    <audio
                      controls
                      src={detail.audioUrl}
                      className="mt-2 w-full"
                      preload="metadata"
                    >
                      <track kind="captions" />
                    </audio>
                  </div>
                ) : null}
                <div className="flex items-start gap-4">
                  {detail.artworkUrl ? (
                    <img
                      src={detail.artworkUrl}
                      alt={t('sermons.artworkAlt', { title: detail.title })}
                      className="size-20 shrink-0 rounded-lg object-cover"
                    />
                  ) : null}
                  <div className="min-w-0 flex-1 space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={
                        detail.status === 'published'
                          ? 'success'
                          : detail.status === 'scheduled'
                            ? 'info'
                            : 'neutral'
                      }
                    >
                      {t(`status.${detail.status}`)}
                    </Badge>
                  </div>
                  <dl className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('sermons.columns.createdAt')}
                      </dt>
                      <dd className="text-body-sm">{formatDateTime(detail.createdAt)}</dd>
                    </div>
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('sermons.columns.publishAt')}
                      </dt>
                      <dd className="text-body-sm">
                        {publishDisplay(detail) ? formatDateTime(publishDisplay(detail)!) : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('sermons.columns.service')}
                      </dt>
                      <dd className="text-body-sm">
                        {serviceLabel(detail.programName, detail.occurrenceDate)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-caption text-fg-secondary">
                        {t('sermons.columns.preacher')}
                      </dt>
                      <dd className="text-body-sm">{detail.preacher ?? '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-caption text-fg-secondary">{t('sermons.preachedOnLabel')}</dt>
                      <dd className="text-body-sm">
                        {detail.preachedOn ? formatDate(detail.preachedOn) : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-caption text-fg-secondary">{t('sermons.versesLabel')}</dt>
                      <dd className="text-body-sm">
                        {detail.scriptureRefs.length > 0
                          ? detail.scriptureRefs.join(' · ')
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                  {detail.description ? (
                    <div>
                      <p className="text-caption text-fg-secondary">{t('sermons.summaryLabel')}</p>
                      <p className="mt-1 whitespace-pre-wrap text-body-sm">{detail.description}</p>
                    </div>
                  ) : null}
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                {detail.status === 'published' ? (
                  <Button
                    variant="outline"
                    loading={unpublish.isPending}
                    onClick={() => unpublish.mutate(detail.id)}
                  >
                    {t('sermons.unpublish')}
                  </Button>
                ) : null}
                {detailShowPublish ? (
                  <Button variant="outline" onClick={() => setPublishTarget(detail)}>
                    {t('sermons.publish')}
                  </Button>
                ) : null}
                {detail.canDelete === true ? (
                  <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
                    <Button variant="outline" onClick={() => setDeleteTarget(detail)}>
                      {tCommon('action.delete')}
                    </Button>
                  </PermissionGate>
                ) : null}
                <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
                  <Button onClick={() => openEdit(detail)}>{tCommon('action.edit')}</Button>
                </PermissionGate>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={publishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setPublishTarget(null);
        }}
        title={t('confirm.publishSermon.title', { name: publishTarget?.title ?? '' })}
        description={t('confirm.publishSermon.description')}
        confirmLabel={t('confirm.publishSermon.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={publish.isPending}
        onConfirm={() => {
          if (publishTarget) publish.mutate(publishTarget.id);
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title={t('confirm.deleteSermon.title', { name: deleteTarget?.title ?? '' })}
        description={t('confirm.deleteSermon.description')}
        confirmLabel={t('confirm.deleteSermon.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />

      <Dialog
        open={formOpen}
        onOpenChange={(next) => {
          setFormOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{editing ? t('sermons.edit') : t('sermons.create')}</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              <div>
                <Label htmlFor="sermon-title">{t('sermons.titleLabel')}</Label>
                <Input
                  id="sermon-title"
                  value={form.title}
                  onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>{t('sermons.serviceLabel')}</Label>
                  <Tip tipId="content.sermon.service" className="mb-2" />
                  <Listbox
                    value={form.programId}
                    onChange={(programId) => {
                      setForm((current) => ({
                        ...current,
                        programId,
                        occurrenceId: null,
                      }));
                    }}
                    options={programOptions}
                    placeholder={t('sermons.servicePlaceholder')}
                    emptyMessage={t('sermons.serviceEmpty')}
                    searchable
                    popoverPortal="dialog"
                  />
                </div>
                <div>
                  <Label>{t('sermons.serviceDateLabel')}</Label>
                  <Tip tipId="content.sermon.serviceDate" className="mb-2" />
                  <Listbox
                    value={form.occurrenceId}
                    onChange={(occurrenceId) => {
                      const occurrence = serviceDates.data?.data.find(
                        (row) => row.id === occurrenceId,
                      );
                      setForm((current) => ({
                        ...current,
                        occurrenceId,
                        preachedOn: occurrence?.occurrenceDate ?? current.preachedOn,
                      }));
                    }}
                    options={dateOptions}
                    placeholder={
                      form.programId
                        ? t('sermons.serviceDatePlaceholder')
                        : t('sermons.serviceDateNeedsService')
                    }
                    emptyMessage={t('sermons.serviceDateEmpty')}
                    disabled={!form.programId}
                    searchable
                    popoverPortal="dialog"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="sermon-preacher">{t('sermons.preacherLabel')}</Label>
                <EntityCombobox
                  id="sermon-preacher"
                  value={speakerId}
                  onChange={(id) => {
                    if (!id) {
                      setSpeakerId(null);
                      setSelectedSpeaker(null);
                      setForm((current) => ({ ...current, preacher: '' }));
                      return;
                    }
                    const option =
                      speakerOptions.find((item) => item.id === id) ??
                      (selectedSpeaker?.id === id ? selectedSpeaker : null);
                    if (!option) return;
                    setSpeakerId(id);
                    setSelectedSpeaker(option);
                    setForm((current) => ({ ...current, preacher: option.label }));
                  }}
                  options={speakerOptions}
                  selected={selectedSpeaker}
                  onQueryChange={setSpeakerQuery}
                  loading={speakers.isPending && !speakers.data}
                  placeholder={t('sermons.preacherPlaceholder')}
                  emptyMessage={t('sermons.preacherEmpty')}
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                  createAction={
                    speakerQuery.trim() &&
                    !speakerOptions.some(
                      (option) =>
                        option.label.toLowerCase() === speakerQuery.trim().toLowerCase(),
                    )
                      ? {
                          label: t('sermons.preacherGuest', { name: speakerQuery.trim() }),
                          onClick: (query) => {
                            const name = query.trim();
                            if (!name) return;
                            const option = { id: guestSpeakerId(name), label: name };
                            setSpeakerId(option.id);
                            setSelectedSpeaker(option);
                            setSpeakerQuery('');
                            setForm((current) => ({ ...current, preacher: name }));
                          },
                        }
                      : undefined
                  }
                />
              </div>

              <div>
                <Label>{t('sermons.preachedOnLabel')}</Label>
                <DatePicker
                  value={form.preachedOn}
                  onChange={(date) => setForm((current) => ({ ...current, preachedOn: date }))}
                  popoverPortal="dialog"
                  placeholder={t('sermons.preachedOnLabel')}
                  className="max-w-xs"
                />
              </div>

              <div className="space-y-3">
                <div>
                  <Label>{t('sermons.versesLabel')}</Label>
                  <Tip tipId="content.sermon.verses" />
                </div>
                {(
                  [
                    ['verse1', t('sermons.verse1Label')],
                    ['verse2', t('sermons.verse2Label')],
                    ['verse3', t('sermons.verse3Label')],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-caption text-fg-secondary">{label}</Label>
                    <ScriptureRefPicker
                      id={`sermon-${key}`}
                      value={form[key]}
                      onChange={(next) =>
                        setForm((current) => ({ ...current, [key]: next }))
                      }
                      allowRange
                      popoverPortal="dialog"
                      labels={scriptureRefPickerLabels(
                        t,
                        tCommon('action.clearSelection'),
                      )}
                    />
                  </div>
                ))}
              </div>

              <div>
                <Label htmlFor="sermon-description">{t('sermons.summaryLabel')}</Label>
                <Textarea
                  id="sermon-description"
                  value={form.description}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, description: e.target.value }))
                  }
                  rows={4}
                  placeholder={t('sermons.summaryPlaceholder')}
                />
              </div>

              <div>
                <Label>{t('sermons.audioLabel')}</Label>
                <Tip tipId="content.sermon.audio" className="mb-2" />
                <FileDropzone
                  accept="audio/mpeg,audio/mp4,audio/aac,audio/ogg,.mp3,.m4a,.aac,.ogg"
                  acceptLabel={t('sermons.audioAccept')}
                  maxBytes={25 * 1024 * 1024}
                  maxSizeLabel={t('sermons.audioMaxSize')}
                  disabled={audioUploading || save.isPending}
                  files={form.audioName ? [{ name: form.audioName }] : undefined}
                  onRemove={() =>
                    setForm((current) => ({
                      ...current,
                      audioKey: null,
                      audioName: null,
                      audioDurationSeconds: null,
                    }))
                  }
                  onFiles={(files) => {
                    const file = files[0];
                    if (!file) return;
                    void (async () => {
                      setAudioUploading(true);
                      try {
                        const duration = await audioDurationSeconds(file);
                        const { key } = await uploadBlob({
                          purpose: 'sermon_audio',
                          blob: file,
                          fileName: file.name,
                        });
                        setForm((current) => ({
                          ...current,
                          audioKey: key,
                          audioName: file.name,
                          audioDurationSeconds: duration,
                        }));
                      } catch (err) {
                        toast.error(
                          err instanceof ApiError
                            ? tErrors(err.code, err.params)
                            : tErrors('UNEXPECTED'),
                        );
                      } finally {
                        setAudioUploading(false);
                      }
                    })();
                  }}
                  labels={{
                    prompt: t('sermons.audioPrompt'),
                    remove: t('sermons.audioRemove'),
                  }}
                />
              </div>

              <div>
                <Label htmlFor="sermon-video">{t('sermons.videoLabel')}</Label>
                <Input
                  id="sermon-video"
                  type="url"
                  value={form.videoUrl}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, videoUrl: e.target.value }))
                  }
                  placeholder={t('sermons.videoPlaceholder')}
                />
              </div>

              <div>
                <Label>{t('sermons.artworkLabel')}</Label>
                <Tip tipId="content.sermon.artwork" className="mb-2" />
                <ChurchLogoUploader
                  layout="inline"
                  src={form.artworkPreviewUrl}
                  name={form.title || t('sermons.title')}
                  onUpload={async (blob) => {
                    const { key } = await uploadBlob({ purpose: 'content_artwork', blob });
                    setForm((current) => ({
                      ...current,
                      artworkKey: key,
                      artworkPreviewUrl: URL.createObjectURL(blob),
                    }));
                  }}
                  onRemove={
                    form.artworkKey
                      ? () =>
                          setForm((current) => ({
                            ...current,
                            artworkKey: null,
                            artworkPreviewUrl: null,
                          }))
                      : undefined
                  }
                  labels={{
                    change: t('sermons.artworkChange'),
                    remove: t('sermons.artworkRemove'),
                    cropTitle: t('sermons.artworkCropTitle'),
                    zoom: tCommon('media.zoom'),
                    apply: t('sermons.artworkApply'),
                    cancel: tCommon('action.cancel'),
                    instructions: t('sermons.artworkInstructions'),
                    closeCropper: tCommon('action.close'),
                  }}
                />
              </div>

              <SwitchField
                id="sermon-downloadable"
                label={t('sermons.downloadableLabel')}
                tipId="content.sermon.downloadable"
                checked={form.isDownloadable}
                onCheckedChange={(checked) =>
                  setForm((current) => ({ ...current, isDownloadable: checked }))
                }
              />

              {editingPublished ? null : (
                <>
              <div>
                <Label>{t('sermons.whenLabel')}</Label>
                <Listbox
                  value={form.publishTiming}
                  onChange={(value) => {
                    const next = (value as PublishTiming) ?? 'draft';
                    setForm((current) => ({
                      ...current,
                      publishTiming: next,
                      publishAt: next === 'draft' ? null : current.publishAt,
                    }));
                  }}
                  options={[
                    { value: 'draft', label: t('sermons.whenDraft') },
                    { value: 'schedule', label: t('sermons.whenSchedule') },
                  ]}
                  popoverPortal="dialog"
                />
                <Tip tipId="content.sermon.when" className="mt-1" />
              </div>

              {form.publishTiming === 'schedule' ? (
                <div>
                  <Label>{t('sermons.publishAtLabel')}</Label>
                  <DateTimePicker
                    value={form.publishAt}
                    onChange={(publishAt) => setForm((current) => ({ ...current, publishAt }))}
                    invalid={scheduleInPast}
                    popoverPortal="dialog"
                    timezoneNotice={(timezone) => t('sermons.timezoneNotice', { timezone })}
                  />
                  {scheduleInPast ? (
                    <p className="mt-1 text-caption text-danger">
                      {t('sermons.publishAtFuture')}
                    </p>
                  ) : null}
                </div>
              ) : null}
                </>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button disabled={!canSave} loading={save.isPending} onClick={() => save.mutate()}>
              {tCommon('action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
